/**
 * Canonical registry of the `data-tour-id` anchor ids that product tours target.
 *
 * Single source of truth for the anchor vocabulary: producer components set
 * `data-tour-id={tourAnchorIds.x}` and tour steps target
 * `toDataTourId(tourAnchorIds.x)`, so an id is never written as a loose
 * string literal. The e2e specs assert `data-tour-anchored="true"` on every
 * targeted step, so a renamed or removed anchor fails the run.
 */
export const tourAnchorIds = {
  mainSidebar: 'main-sidebar',
  sidebarSearch: 'sidebar-search',
  homeSettingsCards: 'home-settings-cards',
  homeSettingsSave: 'home-settings-save',
  createDashboardButton: 'create-dashboard-button',
  inviteUsersButton: 'invite-users-button',
  inventorySaveAsReport: 'inventory-save-as-report',
  useCasesInstallButton: 'use-cases-install-button',
  useCasesSelectSolution: 'use-cases-select-solution',
  homeCardsWorkspaceSettings: 'home-cards-workspace-settings',
  homeCardsEditSettings: 'home-cards-edit-settings',
  inventoryContent: 'inventory-content',
  inventoryColumnsButton: 'inventory-columns-button',
  inventoryDrawer: 'inventory-drawer',
  inventoryDrawerSave: 'inventory-drawer-save',
  orgSettingsHomeSettingsTab: 'org-settings-home-settings-tab',
  homeCardManageData: 'home-card-manage-data',
  inventoryFirstComponentName: 'inventory-first-component-name',
  componentOverviewTabOverview: 'component-overview-tab-overview',
  componentOverviewTabFields: 'component-overview-tab-fields',
  componentOverviewTabReferences: 'component-overview-tab-references',
  componentOverviewTabViewpoints: 'component-overview-tab-viewpoints',
  componentOverviewTabStyles: 'component-overview-tab-styles',
  componentOverviewFieldsPanel: 'component-overview-fields-panel',
  componentOverviewReferencesPanel: 'component-overview-references-panel',
  componentOverviewEditField: 'component-overview-edit-field',
  componentOverviewToggleNavigator: 'component-overview-toggle-navigator',
  componentOverviewNavigatorPanel: 'component-overview-navigator-panel',
  componentOverviewVisualization: 'component-overview-visualization',
  componentOverviewContent: 'component-overview-content',
  componentOverviewRelevantViewpoints: 'component-overview-relevant-viewpoints',
  componentOverviewViewpointListItem: 'component-overview-viewpoint-list-item',
  componentOverviewOpenViewpointButton:
    'component-overview-open-viewpoint-button',
  componentOverviewPriorityFields: 'component-overview-priority-fields',
  componentOverviewViewpointsButton: 'component-overview-viewpoints-button',
  componentOverviewDescriptionSave: 'component-overview-description-save',
  componentOverviewAddReference: 'component-overview-add-reference',
  componentOverviewCreateReferenceSelect:
    'component-overview-create-reference-select',
  componentOverviewCreateReferences: 'component-overview-create-references',
  componentOverviewUpdateDetails: 'component-overview-update-details',
  componentOverviewDrawer: 'component-overview-drawer',
  componentOverviewOpenInNewTab: 'component-overview-open-in-new-tab',
  sidebarDataInventory: 'sidebar-data-inventory',
  dataOverviewGroupingSwitch: 'data-overview-grouping-switch',
  inventorySearchInput: 'inventory-search-input',
  inventoryHierarchyControls: 'inventory-hierarchy-controls',
  inventoryExpandAll: 'inventory-expand-all',
  inventoryFilterBar: 'inventory-filter-bar',
  inventoryRowActions: 'inventory-row-actions',
  inventoryCreateButton: 'inventory-create-button',
  inventorySaveAs: 'inventory-save-as',
  inventoryVisualizeButton: 'inventory-visualize-button',
  sidebarInsights: 'sidebar-insights',
  discoverInventoryWidget: 'discover-inventory-widget',
  discoverQuickLinks: 'discover-quick-links',
  canvasViewpointToolbar: 'canvas-viewpoint-toolbar',
  canvasGraph: 'canvas-graph',
  assetCardFavorite: 'asset-card-favorite',
  presentationSlideList: 'presentation-slide-list',
  presentationSlideDescription: 'presentation-slide-description',
  presentationSlideNavigation: 'presentation-slide-navigation',
  presentationHideNavigator: 'presentation-hide-navigator',
  assetsBrowserSearch: 'assets-browser-search',
  surveyOverviewRow: 'survey-overview-row',
  surveyExistingEntries: 'survey-existing-entries',
  surveyCreateEntry: 'survey-create-entry',
  surveyFormFieldsQuestion: 'survey-form-fields-question',
  surveyFormOwnerQuestion: 'survey-form-owner-question',
  surveyFormSubmit: 'survey-form-submit',
} as const;

export type TourAnchorId = (typeof tourAnchorIds)[keyof typeof tourAnchorIds];

/** CSS selector for a `data-tour-id` anchor. */
export const toDataTourId = (anchorId: TourAnchorId): string =>
  `[data-tour-id="${anchorId}"]`;
